import {
  Box,
  Fade,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import ListItemDefault from '../../../../../components/ListItem/ListItemDefault'
import { maskPercentage } from '../../../../../masks/masks'
import InfoInputAdornment from '../../../../../components/Inputs/InfoInputAdornment'
import SwitchRoundSmall from '../../../../../components/Switch/SwitchRoundSmall'
import { TIndicatedComissionLevelEnum } from '../../../../../core/types/IndicatedTenant'
import IndicatedFunctions from '../../../../../core/functions/IndicatedFunctions'
import { TTenantAnticipationType } from '../../../../../core/types/Tenant'
import { red } from '@mui/material/colors'
import NumberFunctions from '../../../../../core/functions/NumberFunctions'
import { userSettingsAnticipationType } from '../UserSettingConsts'
import { useAdminUserContext } from '../../../../../core/hooks/useAdminUserContext'
import { FeeSectionCard } from '../../../../../components/Cards/FeeSectionCard'

export interface IUserSettingValuesProps {
  errorImmediateWithdrawal: string
  errorAnticipationD15Fee: string
  errorAnticipationD7Fee: string
  errorAnticipationD2Fee: string
  errorIndicatedValue: string
  setErrorAnticipationD2Fee: (value: string) => void
  setErrorAnticipationD15Fee: (value: string) => void
  setErrorAnticipationD7Fee: (value: string) => void
  handleChangeImmediateWithdrawal: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  handleChangeIndicatedComissionsRateValue: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
}

const UserSettingValues = ({
  errorImmediateWithdrawal,
  errorAnticipationD15Fee,
  errorAnticipationD7Fee,
  errorAnticipationD2Fee,
  errorIndicatedValue,
  setErrorAnticipationD2Fee,
  setErrorAnticipationD7Fee,
  setErrorAnticipationD15Fee,
  handleChangeImmediateWithdrawal,
  handleChangeIndicatedComissionsRateValue,
}: IUserSettingValuesProps) => {
  const { tenantSettings, setTenantSettings } = useAdminUserContext()

  const handleChangeAnticipationD2Fee = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!tenantSettings) return

    setErrorAnticipationD2Fee('')
    const taxD2 = NumberFunctions.maskPercentageToNumber(e.target.value)
    setTenantSettings({
      ...tenantSettings,
      anticipationD2Fee: taxD2,
    })
  }

  const handleChangeAnticipationD7Fee = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!tenantSettings) return

    setErrorAnticipationD7Fee('')
    const taxD7 = NumberFunctions.maskPercentageToNumber(e.target.value)
    setTenantSettings({
      ...tenantSettings,
      anticipationD7Fee: taxD7,
    })
  }

  const handleChangeAnticipationD15Fee = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!tenantSettings) return

    setErrorAnticipationD15Fee('')
    const taxD15 = NumberFunctions.maskPercentageToNumber(e.target.value)
    setTenantSettings({
      ...tenantSettings,
      anticipationD15Fee: taxD15,
    })
  }

  return (
    <Stack direction="column" spacing={2} sx={{ py: 1 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Saques e antecipações
        </Typography>

        <Stack spacing={1.5}>
          <ListItemDefault
            title="Saque imediato (%)"
            widthTitlePercentage={40}
            variant="compact"
            value={
              <Box width="100%">
                <OutlinedInput
                  fullWidth
                  size="small"
                  type="text"
                  value={maskPercentage(tenantSettings?.immediateWithdrawal)}
                  onChange={handleChangeImmediateWithdrawal}
                  endAdornment={
                    <InfoInputAdornment title="Percentual para saque especial sobre o valor em garantia. Podendo ser de 0% até 90%." />
                  }
                  error={errorImmediateWithdrawal !== ''}
                />
                {errorImmediateWithdrawal !== '' && (
                  <Typography variant="caption" color={red[700]}>
                    {errorImmediateWithdrawal}
                  </Typography>
                )}
              </Box>
            }
          />

          <ListItemDefault
            title={
              <FormControlLabel
                control={
                  <SwitchRoundSmall
                    sx={{ m: 1 }}
                    checked={tenantSettings?.enableAnticipationD15 ?? false}
                    onChange={(e) => {
                      if (!tenantSettings) return

                      let anticipationD15Fee =
                        tenantSettings?.anticipationD15Fee ?? 0
                      if (anticipationD15Fee <= 0) {
                        anticipationD15Fee = 2
                      }
                      setTenantSettings({
                        ...tenantSettings,
                        enableAnticipationD15: e.target.checked,
                        anticipationD15Fee,
                      })
                    }}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    Habilitar antecipação D + 15
                  </Typography>
                }
                sx={{ width: '100%' }}
              />
            }
            widthTitlePercentage={40}
            variant="compact"
            value={
              <Fade
                in={tenantSettings?.enableAnticipationD15 ?? false}
                mountOnEnter
                unmountOnExit
              >
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="text"
                    value={maskPercentage(
                      tenantSettings?.anticipationD15Fee ?? 0
                    )}
                    onChange={handleChangeAnticipationD15Fee}
                    endAdornment={
                      <InfoInputAdornment title="Percentual de acréscimo nas transações. Podendo ser de 0% até 5%." />
                    }
                    error={errorAnticipationD15Fee !== ''}
                  />
                  {errorAnticipationD15Fee !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorAnticipationD15Fee}
                    </Typography>
                  )}
                </Box>
              </Fade>
            }
          />

          <ListItemDefault
            title={
              <FormControlLabel
                control={
                  <SwitchRoundSmall
                    sx={{ m: 1 }}
                    checked={tenantSettings?.enableAnticipationD7 ?? false}
                    onChange={(e) => {
                      if (!tenantSettings) return

                      let anticipationD7Fee =
                        tenantSettings?.anticipationD7Fee ?? 0
                      if (anticipationD7Fee <= 0) {
                        anticipationD7Fee = 3
                      }
                      setTenantSettings({
                        ...tenantSettings,
                        enableAnticipationD7: e.target.checked,
                        anticipationD7Fee,
                      })
                    }}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    Habilitar antecipação D + 7
                  </Typography>
                }
                sx={{ width: '100%' }}
              />
            }
            widthTitlePercentage={40}
            variant="compact"
            value={
              <Fade
                in={tenantSettings?.enableAnticipationD7 ?? false}
                mountOnEnter
                unmountOnExit
              >
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="text"
                    value={maskPercentage(
                      tenantSettings?.anticipationD7Fee ?? 0
                    )}
                    onChange={handleChangeAnticipationD7Fee}
                    endAdornment={
                      <InfoInputAdornment title="Percentual de acréscimo nas transações. Podendo ser de 0% até 5%." />
                    }
                    error={errorAnticipationD7Fee !== ''}
                  />
                  {errorAnticipationD7Fee !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorAnticipationD7Fee}
                    </Typography>
                  )}
                </Box>
              </Fade>
            }
          />

          <ListItemDefault
            title={
              <FormControlLabel
                control={
                  <SwitchRoundSmall
                    sx={{ m: 1 }}
                    checked={tenantSettings?.enableAnticipationD2 ?? false}
                    onChange={(e) => {
                      if (!tenantSettings) return

                      let anticipationD2Fee =
                        tenantSettings?.anticipationD2Fee ?? 0
                      if (anticipationD2Fee <= 0) {
                        anticipationD2Fee = 4
                      }
                      setTenantSettings({
                        ...tenantSettings,
                        enableAnticipationD2: e.target.checked,
                        anticipationD2Fee,
                      })
                    }}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    Habilitar antecipação D + 2
                  </Typography>
                }
                sx={{ width: '100%' }}
              />
            }
            widthTitlePercentage={40}
            variant="compact"
            value={
              <Fade
                in={tenantSettings?.enableAnticipationD2 ?? false}
                mountOnEnter
                unmountOnExit
              >
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="text"
                    value={maskPercentage(
                      tenantSettings?.anticipationD2Fee ?? 0
                    )}
                    onChange={handleChangeAnticipationD2Fee}
                    endAdornment={
                      <InfoInputAdornment title="Taxa de reajuste na taxa de parcelamento. Podendo ser de 0% até 5%." />
                    }
                    error={errorAnticipationD2Fee !== ''}
                  />
                  {errorAnticipationD2Fee !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorAnticipationD2Fee}
                    </Typography>
                  )}
                </Box>
              </Fade>
            }
          />

          <ListItemDefault
            title="Antecipação selecionada pelo usuário"
            widthTitlePercentage={40}
            variant="compact"
            value={
              userSettingsAnticipationType[
                tenantSettings?.anticipationType ?? TTenantAnticipationType.None
              ]
            }
          />
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Taxas por meio de pagamento
        </Typography>

        <Stack spacing={2}>
          <FeeSectionCard
            title="Captura"
            subtitle="Defina regras específicas de captura por forma de pagamento. Use “Normal” para seguir a regra padrão da plataforma."
            rows={[
              {
                label: 'Pix',
                field: 'capturePixFee',
                type: 'currency',
                infoTitle:
                  'Valor fixo de captura para pagamentos via Pix. Exemplo: 0,50.',
              },
              {
                label: 'Cartão',
                field: 'captureCardFee',
                type: 'currency',
                infoTitle:
                  'Valor fixo de captura para pagamentos via Cartão. Exemplo: 0,50.',
              },
              {
                label: 'Boleto',
                field: 'captureBoletoFee',
                type: 'currency',
                infoTitle:
                  'Valor fixo de captura para pagamentos via Boleto. Deixe como Normal para usar a regra padrão.',
              },
            ]}
          />

          <FeeSectionCard
            title="MDR"
            subtitle="Defina regras específicas de MDR por forma de pagamento. Use “Normal” para seguir a regra padrão da plataforma."
            rows={[
              {
                label: 'Pix',
                field: 'mdrPixFee',
                type: 'percentage',
                infoTitle:
                  'Percentual de MDR para pagamentos via Pix. Exemplo: 1%.',
              },
              {
                label: 'Cartão',
                field: 'mdrCardFee',
                type: 'percentage',
                infoTitle:
                  'Percentual de MDR para pagamentos via Cartão. Deixe como Normal para usar a regra padrão.',
              },
              {
                label: 'Boleto',
                field: 'mdrBoletoFee',
                type: 'percentage',
                infoTitle:
                  'Percentual de MDR para pagamentos via Boleto. Exemplo: 1%.',
              },
            ]}
          />
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Indicações
        </Typography>

        <ListItemDefault
          title={
            <FormControlLabel
              control={
                <SwitchRoundSmall
                  sx={{ m: 1 }}
                  checked={tenantSettings?.disableIndicatedComissions ?? false}
                  onChange={(e) => {
                    if (!tenantSettings) return

                    let anticipationD2Fee =
                      tenantSettings?.anticipationD2Fee ?? 0
                    if (anticipationD2Fee <= 0) {
                      anticipationD2Fee = 4
                    }
                    setTenantSettings({
                      ...tenantSettings,
                      disableIndicatedComissions: e.target.checked,
                      anticipationD2Fee,
                    })
                  }}
                />
              }
              label={
                <Typography variant="subtitle2">
                  Desabilitar sistema indicações
                </Typography>
              }
              sx={{ width: '100%' }}
            />
          }
          widthTitlePercentage={40}
          variant="compact"
          value={
            <Fade
              in={!(tenantSettings?.disableIndicatedComissions ?? false)}
              mountOnEnter
              unmountOnExit
            >
              <Stack direction="row" spacing={2} width="100%">
                <FormControl fullWidth size="small">
                  <InputLabel id="days-select-label">Nível</InputLabel>
                  <Select
                    variant="outlined"
                    labelId="days-select-label"
                    label="Nível"
                    value={
                      tenantSettings?.indicatedComissionLevel ??
                      TIndicatedComissionLevelEnum.None
                    }
                    onChange={(e) => {
                      if (!tenantSettings) return

                      const indicatedComissionLevel = e.target
                        .value as TIndicatedComissionLevelEnum
                      const indicatedComissionsRateValue =
                        indicatedComissionLevel ===
                        TIndicatedComissionLevelEnum.Basic
                          ? 5
                          : indicatedComissionLevel ===
                              TIndicatedComissionLevelEnum.Gold
                            ? 10
                            : indicatedComissionLevel ===
                                TIndicatedComissionLevelEnum.Platinum
                              ? 15
                              : 0

                      setTenantSettings({
                        ...tenantSettings,
                        indicatedComissionLevel,
                        indicatedComissionsRateValue,
                      })
                    }}
                  >
                    {IndicatedFunctions.indicatedComissionLevels.map(
                      (level) => (
                        <MenuItem key={level} value={level}>
                          {IndicatedFunctions.getIndicatedComissionLevelString(
                            level
                          )}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>

                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="text"
                    value={maskPercentage(
                      tenantSettings?.indicatedComissionsRateValue ?? 0
                    )}
                    onChange={handleChangeIndicatedComissionsRateValue}
                    endAdornment={
                      <InfoInputAdornment title="Percentual a receber de comissão para cada indicado cadastrado. Podendo ser de 0% até 30%." />
                    }
                    error={errorIndicatedValue !== ''}
                  />
                  {errorIndicatedValue !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorIndicatedValue}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Fade>
          }
        />
      </Box>
    </Stack>
  )
}

export default UserSettingValues
